import { Injectable } from '@angular/core';

export type BotOwnerViewAs = 'actual' | 'owner_admin' | 'moderator' | 'organizer' | 'user';
export type BotOwnerPreviewRole = Exclude<BotOwnerViewAs, 'actual'>;

@Injectable({
  providedIn: 'root',
})
export class BotOwnerPreviewService {
  // v2: previous default was owner_admin, which forced every bot-owner session into read-only preview.
  private readonly storageKey = 'raidOrganizer.botOwner.viewAs.v2';
  private readonly previewRoles: BotOwnerPreviewRole[] = ['owner_admin', 'moderator', 'organizer', 'user'];
  private readonly validViewAs: BotOwnerViewAs[] = ['actual', ...this.previewRoles];

  isWebsiteBotOwner = false;

  setWebsiteBotOwner(value: boolean): void {
    this.isWebsiteBotOwner = value;
  }

  getViewAs(): BotOwnerViewAs {
    const storedValue = typeof window !== 'undefined' ? window.localStorage.getItem(this.storageKey) : null;
    if (this.isValidViewAs(storedValue)) {
      return storedValue;
    }
    return 'actual';
  }

  setViewAs(value: BotOwnerViewAs): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(this.storageKey, value);
  }

  /** Restore edit mode. Used when returning to guild selection so preview does not leak across guilds. */
  clearPreview(): void {
    this.setViewAs('actual');
  }

  get activeViewAs(): BotOwnerPreviewRole | null {
    if (!this.isWebsiteBotOwner) {
      return null;
    }
    const viewAs = this.getViewAs();
    return this.isPreviewRole(viewAs) ? viewAs : null;
  }

  private isValidViewAs(value: string | null): value is BotOwnerViewAs {
    return this.validViewAs.includes(value as BotOwnerViewAs);
  }

  private isPreviewRole(value: BotOwnerViewAs): value is BotOwnerPreviewRole {
    return this.previewRoles.includes(value as BotOwnerPreviewRole);
  }
}
